import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Check, Loader2, Sparkles, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export interface ChatAction {
  type: string;
  table?: string;
  id?: string;
  fields?: Record<string, any>;
  label?: string;
}

function normalizeAction(action: ChatAction): ChatAction {
  const ALLOWED = new Set([
    'name', 'slug', 'location', 'description',
    'start_date', 'end_date', 'status',
    'image_url', 'thumbnail_url',
    'max_participants', 'capacity',
    'ticket_url', 'website_url', 'external_link', 'canonical_url', 'theme',
  ]);

  const incoming = (action.fields && typeof action.fields === 'object') ? { ...action.fields } : {};
  // Map common synonyms the model may emit into allowed columns.
  const synonyms: Record<string, string> = {
    url: 'website_url',
    website: 'website_url',
    link: 'website_url',
    homepage: 'website_url',
  };
  for (const [k, v] of Object.entries(incoming)) {
    if (ALLOWED.has(k)) continue;
    const target = synonyms[k.toLowerCase()];
    if (target && !(target in incoming)) incoming[target] = v;
    delete incoming[k];
  }

  const label = action.label || '';
  if (!incoming.name) {
    const nameMatch =
      label.match(/Name\s+auf\s+['"“”]?(.+?)['"“”]?(?:\s+und\s+Ort|\s+ändern|$)/i) ||
      label.match(/Event\s+(?:in|zu|auf)\s+['"“”]?(.+?)['"“”]?\s+umbenennen/i);
    if (nameMatch?.[1]) incoming.name = cleanActionValue(nameMatch[1]);
  }
  if (!incoming.location) {
    const locationMatch = label.match(/(?:Ort|Location|Place)\s+auf\s+['"“”]?(.+?)['"“”]?(?:\s+ändern|$)/i);
    if (locationMatch?.[1]) incoming.location = cleanActionValue(locationMatch[1]);
  }
  // Try to recover a URL from the label if no link fields are present.
  const hasLinkField = ['website_url', 'canonical_url', 'external_link', 'ticket_url']
    .some((k) => typeof incoming[k] === 'string' && /^https?:\/\//i.test(incoming[k] as string));
  if (!hasLinkField) {
    const urlMatch = label.match(/(https?:\/\/[^\s)"'„“”]+)/i);
    if (urlMatch?.[1]) incoming.website_url = urlMatch[1];
  }

  // Drop anything not in the allowlist.
  const fields: Record<string, any> = {};
  for (const [k, v] of Object.entries(incoming)) {
    if (ALLOWED.has(k) && v !== undefined && v !== null && v !== '') fields[k] = v;
  }
  return { ...action, fields };
}

function cleanActionValue(value: string) {
  return value.trim().replace(/^['"`“”]+|['"`“”.]+$/g, '').trim();
}

interface Props {
  action: ChatAction;
}

export function ChatActionButton({ action }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'idle' | 'applying' | 'done' | 'error' | 'dismissed'>('idle');

  const actionToApply = normalizeAction(action);
  const fieldEntries = Object.entries(actionToApply.fields || {});
  const hasUsableFields = fieldEntries.length > 0;

  const apply = async () => {
    setStatus('applying');
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData.user) {
        toast({ title: 'Login required', description: 'Sign in as admin to apply changes.', variant: 'destructive' });
        setStatus('error');
        return;
      }
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (sessionError || !accessToken) {
        throw new Error('Keine aktive Session. Bitte neu einloggen.');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/apply-chatbot-action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(actionToApply),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || `Anfrage fehlgeschlagen (${response.status})`);
      }
      if (data?.error) throw new Error(data.error);
      setStatus('done');
      await queryClient.invalidateQueries({ queryKey: ['event-editor', actionToApply.id] });
      window.dispatchEvent(new CustomEvent('chatbot-action-applied', { detail: data }));
      toast({ title: 'Übernommen', description: actionToApply.label || 'Event aktualisiert.' });
    } catch (e) {
      setStatus('error');
      toast({
        title: 'Konnte nicht übernommen werden',
        description: e instanceof Error ? e.message : 'Unbekannter Fehler',
        variant: 'destructive',
      });
    }
  };

  if (status === 'dismissed') return null;

  return (
    <div className="my-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
      <div className="flex items-center gap-2 mb-2 text-sm font-medium">
        <Sparkles className="h-4 w-4 text-primary" />
        <span>{action.label || 'Vorgeschlagene Änderung'}</span>
      </div>
      {fieldEntries.length > 0 && (
        <ul className="text-xs text-muted-foreground mb-2 space-y-0.5">
          {fieldEntries.map(([k, v]) => (
            <li key={k}>
              <span className="font-medium text-foreground">{k}:</span> {String(v)}
            </li>
          ))}
        </ul>
      )}
      {status === 'done' ? (
        <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
          <Check className="h-3.5 w-3.5" /> Übernommen
        </div>
      ) : (
        <div className="flex gap-2">
          <Button size="sm" onClick={apply} disabled={status === 'applying' || !hasUsableFields}>
            {status === 'applying' ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Speichere…</>
            ) : (
              <><Check className="h-3.5 w-3.5 mr-1" />Bestätigen</>
            )}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setStatus('dismissed')} disabled={status === 'applying'}>
            <X className="h-3.5 w-3.5 mr-1" />Abbrechen
          </Button>
        </div>
      )}
      {!hasUsableFields && status !== 'done' && (
        <p className="mt-2 text-xs text-muted-foreground">
          Es wurde kein gültiges Feld erkannt. Bitte formuliere die Änderung konkret, z.B. „Setze website_url auf https://…“.
        </p>
      )}
    </div>
  );
}

/**
 * Parse fenced ```action ... ``` JSON blocks out of an assistant message.
 * Returns the cleaned text (without the fences) and the list of actions.
 */
export function parseChatActions(content: string): { text: string; actions: ChatAction[] } {
  const re = /```action\s*\n([\s\S]*?)```/gi;
  const actions: ChatAction[] = [];
  let cleaned = content.replace(re, (_m, body) => {
    try {
      const parsed = JSON.parse(body.trim());
      if (parsed && typeof parsed === 'object') actions.push(parsed as ChatAction);
    } catch {
      // Likely still streaming – leave a small placeholder so user sees something
    }
    return '';
  });
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();
  return { text: cleaned, actions };
}